import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { CSSProperties, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import FlatLayout from 'components/layouts/flatLayout'
import PageTitleComponent from 'components/parts/pageTitle'
import { securitiesSecurityFieldApi } from 'hooks/api/securitiesSecurityFields'
import { securityApi } from 'hooks/api/security'
import { securityFieldApi } from 'hooks/api/securityField'
import { workPatternApi } from 'hooks/api/workPattern'
import FixedSecuritySettingComponent from 'pages/securityField/settings/components/fixedSecuritySetting'
import SectionComponent from 'pages/securityField/settings/components/section'
import SingleChangeFormComponent from 'pages/securityField/settings/components/singleChangeForm'
import WorkPatternSettingComponent from 'pages/securityField/settings/components/workPatternSetting'
import { addSnackbar } from 'store/snackbarSlice'
import { PrimaryWorkPatternContent } from 'types/primaryWorkPattern'
import {
  FixedSecurity,
  RoleType,
  RoleTypeKey,
  SecuritiesSecurityField,
} from 'types/securitiesSecurityFields'
import { Security } from 'types/security'
import { SecurityField } from 'types/securityField'
import { WorkPatternContent } from 'types/workPattern'

const apiCall = {
  getSecurities: async () => {
    const response = await securityApi().filter()
    return response.data
  },
  getSecurityFields: async () => {
    const response = await securityFieldApi().filter()
    return response.data
  },
  getWorkPatterns: async (securityFieldId: string) => {
    const response =
      await workPatternApi().filterBySecurityField(securityFieldId)
    return response.data.workPatterns
  },
  getRelations: async (securityFieldId: string) => {
    const response =
      await securitiesSecurityFieldApi().filterBySecurityField(securityFieldId)

    return response.data.relations
  },

  editSecurityField: {
    update: async (securityField: SecurityField) => {
      await securityFieldApi().update(securityField)
    },
  },

  editWorkPattern: {
    create: async (
      securityFieldId: string,
      workPattern: WorkPatternContent,
    ) => {
      await workPatternApi().create(securityFieldId, workPattern)
    },
    update: async (
      id: string,
      securityFieldId: string,
      workPattern: WorkPatternContent,
    ) => {
      await workPatternApi().update(id, securityFieldId, workPattern)
    },
    bulkDelete: async (securityFieldId: string, workPatternIds: string[]) => {
      await workPatternApi().bulkDelete(securityFieldId, { workPatternIds })
    },
  },

  editFixedSecurity: {
    create: async (
      securityFieldId: string,
      fixedSecurity: {
        securityId: string
        primaryWorks: PrimaryWorkPatternContent[]
      },
    ) => {
      await securitiesSecurityFieldApi().create({
        securityFieldId,
        securityId: fixedSecurity.securityId,
        primaryWorkIds: fixedSecurity.primaryWorks.map(
          (work) => work.workPatternId,
        ),
      })
    },
    update: async (id: string, primaryWorks: PrimaryWorkPatternContent[]) => {
      await securitiesSecurityFieldApi().update(id, {
        primaryWorkIds: primaryWorks.map((work) => work.workPatternId),
      })
    },
    bulkUpdate: async (relations: SecuritiesSecurityField[]) => {
      await securitiesSecurityFieldApi().bulkUpdate({
        relations,
      })
    },
    bulkDelete: async (securityFieldId: string, ids: string[]) => {
      await securitiesSecurityFieldApi().bulkDelete(securityFieldId, {
        ids,
      })
    },
  },
}

const shiftShowURL = (securityFieldId: string) =>
  `/shift/show/${securityFieldId}`

export default function SecurityFieldSettingsPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { securityFieldId } = useParams<{ securityFieldId: string }>()
  if (securityFieldId == null) {
    // 404 にリダイレクト
    return <Navigate to="/404" />
  }

  const [securityField, setSecurityField] = useState<SecurityField | null>(null)

  const [workPatterns, setWorkPatterns] = useState<
    (WorkPatternContent & { id: string })[]
  >([])

  const [securities, setSecurities] = useState<Security[]>([])
  const [fixedSecurities, setFixedSecurities] = useState<FixedSecurity[]>([])

  const refresh = async () => {
    if (securityFieldId == null) return

    const securities = await apiCall.getSecurities()
    const fields = await apiCall.getSecurityFields()
    const workPatterns = await apiCall.getWorkPatterns(securityFieldId)
    const fixedSecurities = await apiCall.getRelations(securityFieldId)

    // url id -> entity
    fields.forEach((field) => {
      if (field.id === securityFieldId) {
        setSecurityField(field)
      }
    })

    setWorkPatterns(workPatterns)
    setSecurities(securities)
    setFixedSecurities(fixedSecurities)
  }

  useEffect(() => {
    refresh().then()
  }, [])

  const handleNavShift = () => {
    if (securityFieldId == null) return
    navigate(shiftShowURL(securityFieldId))
  }

  const securityFieldHandlers = {
    updateName: (name: string) => {
      const apiAction = async () => {
        if (securityField == null) return
        await apiCall.editSecurityField.update({
          ...securityField,
          name,
        })
        await refresh()
      }
      apiAction().then()
    },
    updatePhone: (phone: string) => {
      const apiAction = async () => {
        if (securityField == null) return
        await apiCall.editSecurityField.update({
          ...securityField,
          phone,
        })
        await refresh()
      }
      apiAction().then()
    },
    updateEmail: (email: string) => {
      const apiAction = async () => {
        if (securityField == null) return
        await apiCall.editSecurityField.update({
          ...securityField,
          email,
        })
        await refresh()
      }
      apiAction().then()
    },
  }

  const workPatternHandlers = {
    bulkDelete: (ids: string[]) =>
      (async () => {
        await apiCall.editWorkPattern.bulkDelete(securityFieldId, ids)
        await refresh()
      })().then(() => {
        dispatch(addSnackbar({ message: '削除しました', severity: 'success' }))
      }),
    add: (workPattern: WorkPatternContent) =>
      (async () => {
        await apiCall.editWorkPattern.create(securityFieldId, workPattern)
        await refresh()
      })().then(() => {
        dispatch(addSnackbar({ message: '追加しました', severity: 'success' }))
      }),
    update: (id: string, workPattern: WorkPatternContent) =>
      (async () => {
        await apiCall.editWorkPattern.update(id, securityFieldId, workPattern)
        await refresh()
      })().then(() => {
        dispatch(addSnackbar({ message: '更新しました', severity: 'success' }))
      }),
  }

  const fixedSecurityHandlers = {
    bulkDelete: (ids: string[]) =>
      (async () => {
        await apiCall.editFixedSecurity.bulkDelete(securityFieldId, ids)
        await refresh()
      })().then(() => {
        dispatch(addSnackbar({ message: '削除しました', severity: 'success' }))
      }),
    add: (data: {
      securityId: string
      primaryWorks: PrimaryWorkPatternContent[]
    }) =>
      (async () => {
        await apiCall.editFixedSecurity.create(securityFieldId, data)
        await refresh()
      })().then(() => {
        dispatch(addSnackbar({ message: '追加しました', severity: 'success' }))
      }),
    update: (id: string, data: PrimaryWorkPatternContent[]) =>
      (async () => {
        await apiCall.editFixedSecurity.update(id, data)
        await refresh()
      })().then(() => {
        dispatch(addSnackbar({ message: '更新しました', severity: 'success' }))
      }),
    updateRole:
      (roleType: RoleTypeKey) =>
      (toUpdateRelationIds: string[], toNoneRelationIds: string[]) =>
        (async () => {
          const toUpdateRelationIdsSet = new Set(toUpdateRelationIds)
          const toNoneRelationIdsSet = new Set(toNoneRelationIds)

          const toUpdateRelations = fixedSecurities
            .filter((relation) => toUpdateRelationIdsSet.has(relation.id))
            .map((relation) => ({
              ...relation,
              roleType: roleType,
            }))

          const toNoneRelations = fixedSecurities
            .filter((relation) => toNoneRelationIdsSet.has(relation.id))
            .map((relation) => ({
              ...relation,
              roleType: RoleType.RoleNone,
            }))
          await apiCall.editFixedSecurity.bulkUpdate(
            toUpdateRelations.concat(toNoneRelations),
          )
          await refresh()
        })().then(),
  }

  return (
    <FlatLayout title="警備員設定" style={contentStyle}>
      <PageTitleComponent>警備先情報設定</PageTitleComponent>
      <Box style={formContainerStyle}>
        <Typography>警備先名</Typography>
        {securityField && (
          <SingleChangeFormComponent
            id="securityFieldName"
            defaultValue={securityField.name}
            onSave={securityFieldHandlers.updateName}
          />
        )}
        <Button variant="outlined" onClick={handleNavShift}>
          シフト確認
        </Button>
      </Box>
      {securityField && (
        <Box>
          <WorkPatternSettingComponent
            workPatterns={workPatterns}
            onDelete={workPatternHandlers.bulkDelete}
            onAdd={workPatternHandlers.add}
            onUpdate={workPatternHandlers.update}
          />
          <FixedSecuritySettingComponent
            workPatterns={workPatterns}
            securities={securities}
            fixedSecurities={fixedSecurities}
            onDelete={fixedSecurityHandlers.bulkDelete}
            onAdd={fixedSecurityHandlers.add}
            onUpdate={fixedSecurityHandlers.update}
            onUpdateLeaderRole={fixedSecurityHandlers.updateRole(
              RoleType.RoleLeader,
            )}
          />
          <SectionComponent title="携帯情報">
            <Box style={mobileSettingStyle}>
              <Box style={formContainerStyle}>
                <Typography>携帯番号</Typography>
                <SingleChangeFormComponent
                  id="securityFieldPhone"
                  defaultValue={securityField.phone}
                  onSave={securityFieldHandlers.updatePhone}
                />
              </Box>

              <Box style={formContainerStyle}>
                <Typography>メールアドレス</Typography>
                <SingleChangeFormComponent
                  id="securityFieldName"
                  defaultValue={securityField.email}
                  onSave={securityFieldHandlers.updateEmail}
                />
              </Box>
            </Box>
          </SectionComponent>
        </Box>
      )}
    </FlatLayout>
  )
}

const contentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: 0,
  overflowY: 'scroll',
}

const formContainerStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  marginBottom: '16px',
  alignItems: 'center',
}

const mobileSettingStyle: CSSProperties = {
  display: 'flex',
  gap: '16px',
}
